import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, TablePagination, Select, MenuItem, FormControl,
  InputLabel, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Skeleton, TextField, IconButton } from '@mui/material';
import { Visibility, Search } from '@mui/icons-material';
import { viewerAPI } from '../../services/api';
import dayjs from 'dayjs';
import { useNotify } from '../../utils/notify';

var STATUS_COLORS = {
  'pending': 'default',
  'travel booking completed': 'info',
  'accommodation confirmed': 'primary',
  'included in clearance': 'warning',
  'all arrangements finalised': 'success',
};

function getStatusColor(status) {
  return STATUS_COLORS[status] || 'default';
}

export default function ViewerRegistrations() {
  var cx = useState(null); var context = cx[0]; var setContext = cx[1];
  var fs = useState(''); var formId = fs[0]; var setFormId = fs[1];
  var rs = useState([]); var rows = rs[0]; var setRows = rs[1];
  var ts = useState(0); var total = ts[0]; var setTotal = ts[1];
  var ps = useState(0); var page = ps[0]; var setPage = ps[1];
  var pp = useState(25); var perPage = pp[0]; var setPerPage = pp[1];
  var ds = useState(null); var detail = ds[0]; var setDetail = ds[1];
  var ld = useState(true); var loading = ld[0]; var setLoading = ld[1];
  var ss = useState(''); var search = ss[0]; var setSearch = ss[1];
  var sq = useState(''); var searchQuery = sq[0]; var setSearchQuery = sq[1];
  var sts = useState({}); var statusesByForm = sts[0]; var setStatusesByForm = sts[1];

  var notify = useNotify();
  var E = React.createElement;

  useEffect(function() {
    viewerAPI.context().then(function(r) {
      var c = r.data.data;
      setContext(c);
      if (c.registration_statuses) setStatusesByForm(c.registration_statuses);
      // Filter to registration forms only
      var regForms = (c.forms || []).filter(function(f) { return f.form_type === 'registration'; });
      if (regForms.length > 0) setFormId(regForms[0].id);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, []);

  useEffect(function() {
    if (!formId) return;
    var params = { form_id: formId, page: page + 1, per_page: perPage };
    if (searchQuery) params.search = searchQuery;
    viewerAPI.registrations(params)
      .then(function(r) {
        setRows(r.data.data.data || []);
        setTotal(r.data.data.total || 0);
      }).catch(function() { setRows([]); });
  }, [formId, page, perPage, searchQuery]);

  function openDetail(id) {
    viewerAPI.registration(id).then(function(r) { setDetail(r.data.data); });
  }

  function handleStatusChange(regId, newStatus) {
    viewerAPI.updateRegistrationStatus(regId, newStatus)
      .then(function() {
        // Update the row locally
        setRows(function(prev) {
          return prev.map(function(row) {
            if (row.id === regId) {
              return Object.assign({}, row, { status: newStatus, status_updated_at: new Date().toISOString() });
            }
            return row;
          });
        });
        // Update detail if open
        if (detail && detail.id === regId) {
          setDetail(function(prev) {
            return Object.assign({}, prev, { status: newStatus, status_updated_at: new Date().toISOString() });
          });
        }
        notify('Status updated', {variant: 'success'});
      }).catch(function(err) {
        var msg = (err.response && err.response.data && err.response.data.error) || 'Failed to update status';
        notify(msg, {variant: 'error'});
      });
  }

  function handleSearch() {
    setSearchQuery(search);
    setPage(0);
  }

  if (loading) return E(Skeleton, {variant:'rectangular', height:300, sx:{borderRadius:2}});

  var allForms = (context && context.forms) || [];
  var regForms = allForms.filter(function(f) { return f.form_type === 'registration'; });

  if (regForms.length === 0) {
    return E(Card, null, E(CardContent, {sx:{textAlign:'center', py:8}},
      E(Typography, {variant:'h6', sx:{mb:1}}, 'No registration forms shared with you'),
      E(Typography, {color:'text.secondary'},
        'An administrator decides which forms you can see.')));
  }

  // Parse schema for detail view
  var schema = null;
  if (detail) {
    schema = typeof detail.form_schema === 'string' ? JSON.parse(detail.form_schema) : detail.form_schema;
    if (typeof schema === 'string') { try { schema = JSON.parse(schema); } catch (e) { schema = {}; } }
  }
  var fields = schema ? (schema.fields || []).slice().sort(function(a,b){ return (a.order||0)-(b.order||0); }) : [];
  var sections = schema ? (schema.sections || []).slice().sort(function(a,b){ return (a.order||0)-(b.order||0); }) : [];

  var defaultStatuses = ['pending', 'travel booking completed', 'accommodation confirmed', 'included in clearance', 'all arrangements finalised'];
  var currentFormStatuses = (statusesByForm && statusesByForm[formId]) || defaultStatuses;
  var availableStatuses = (detail && detail.available_statuses) || currentFormStatuses;

  return E(Box, null,
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center',
      flexWrap:'wrap', gap:2, mb:2}},
      E(Typography, {variant:'h4'}, 'Registrations'),
      E(Box, {sx:{display:'flex', gap:2, alignItems:'center', flexWrap:'wrap'}},
        // Search
        E(TextField, {size:'small', placeholder:'Search by name or email', value:search,
          onChange:function(e){ setSearch(e.target.value); },
          onKeyDown:function(e){ if (e.key === 'Enter') handleSearch(); },
          sx:{minWidth:200},
          InputProps:{endAdornment: E(IconButton, {size:'small', onClick:handleSearch}, E(Search, {fontSize:'small'}))}}),
        // Form selector
        regForms.length > 1
          ? E(FormControl, {size:'small', sx:{minWidth:240}},
              E(InputLabel, null, 'Form'),
              E(Select, {value:formId, label:'Form',
                onChange:function(e){ setFormId(e.target.value); setPage(0); setSearchQuery(''); setSearch(''); }},
                regForms.map(function(f) { return E(MenuItem, {key:f.id, value:f.id}, f.title); })))
          : null)),

    (context && !context.can_see_identity)
      ? E(Alert, {severity:'info', sx:{mb:2}},
          'Names and emails are hidden from your account.')
      : null,

    E(Card, null,
      E(TableContainer, null,
        E(Table, {size:'small'},
          E(TableHead, null,
            E(TableRow, null,
              E(TableCell, null, 'Name'),
              context && context.can_see_identity ? E(TableCell, null, 'Email') : null,
              E(TableCell, null, 'Type'),
              E(TableCell, null, 'Status'),
              E(TableCell, null, 'Registered'),
              E(TableCell, {align:'right'}, 'Actions'))),
          E(TableBody, null,
            rows.map(function(reg) {
              return E(TableRow, {key:reg.id, hover:true},
                E(TableCell, null, reg.name || '-'),
                context && context.can_see_identity ? E(TableCell, null, reg.email) : null,
                E(TableCell, null, E(Chip, {label:reg.participant_type === 'scholar' ? 'Scholar' : 'Self-funded',
                  size:'small', color:reg.participant_type === 'scholar' ? 'primary' : 'default', variant:'outlined'})),
                E(TableCell, null,
                  E(FormControl, {size:'small', sx:{minWidth:180}},
                    E(Select, {value:reg.status || 'pending', size:'small',
                      onChange:function(e){ handleStatusChange(reg.id, e.target.value); },
                      renderValue:function(val) { return E(Chip, {label:val, size:'small', color:getStatusColor(val)}); }},
                      currentFormStatuses.map(function(s) {
                        return E(MenuItem, {key:s, value:s}, s);
                      })))),
                E(TableCell, null, dayjs(reg.registered_at).format('MMM D, YYYY')),
                E(TableCell, {align:'right'},
                  E(Button, {size:'small', startIcon:E(Visibility),
                    onClick:function(){ openDetail(reg.id); }}, 'View')));
            }),
            rows.length === 0
              ? E(TableRow, null, E(TableCell, {colSpan:6, align:'center',
                  sx:{py:4, color:'text.secondary'}}, 'No registrations'))
              : null))),
      E(TablePagination, {component:'div', count:total, page:page, rowsPerPage:perPage,
        rowsPerPageOptions:[25, 50, 100],
        onPageChange:function(_, p){ setPage(p); },
        onRowsPerPageChange:function(e){ setPerPage(parseInt(e.target.value, 10)); setPage(0); }})),

    // Detail dialog
    detail ? E(Dialog, {open:true, onClose:function(){ setDetail(null); }, maxWidth:'md', fullWidth:true},
      E(DialogTitle, null,
        E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center'}},
          E(Typography, {variant:'h6'}, detail.name || 'Registrant'),
          E(Chip, {label:detail.participant_type === 'scholar' ? 'Scholar' : 'Self-funded',
            color:detail.participant_type === 'scholar' ? 'primary' : 'default', size:'small'}))),
      E(DialogContent, {dividers:true},
        // Status changer
        E(Box, {sx:{mb:3, p:2, bgcolor:'grey.50', borderRadius:1}},
          E(Typography, {variant:'subtitle2', sx:{mb:1}}, 'Registration Status'),
          E(FormControl, {size:'small', sx:{minWidth:260}},
            E(Select, {value:detail.status || 'pending',
              onChange:function(e){ handleStatusChange(detail.id, e.target.value); }},
              availableStatuses.map(function(s) {
                return E(MenuItem, {key:s, value:s},
                  E(Chip, {label:s, size:'small', color:getStatusColor(s), sx:{mr:1}}), s);
              }))),
          detail.status_updated_at
            ? E(Typography, {variant:'caption', color:'text.secondary', sx:{mt:1, display:'block'}},
                'Last updated: ' + dayjs(detail.status_updated_at).format('MMM D, YYYY h:mm A'))
            : null),

        // Identity info
        context && context.can_see_identity ? E(Box, {sx:{mb:2}},
          detail.email ? E(Typography, {variant:'body2', color:'text.secondary'}, 'Email: ' + detail.email) : null) : null,

        // Form responses
        sections.length > 0
          ? sections.map(function(sec) {
              var secFields = fields.filter(function(f) {
                return f.section_id === sec.id && detail.responses && detail.responses[f.id] !== undefined;
              });
              if (secFields.length === 0) return null;
              return E(Box, {key:sec.id, sx:{mb:3}},
                E(Typography, {variant:'subtitle1', fontWeight:700, color:'primary.main',
                  sx:{borderBottom:1, borderColor:'divider', pb:0.5, mb:1.5}}, sec.title),
                secFields.map(function(f) {
                  var v = detail.responses[f.id];
                  return E(Box, {key:f.id, sx:{mb:2}},
                    E(Typography, {variant:'subtitle2', color:'text.secondary', fontWeight:600}, f.label),
                    E(Typography, {variant:'body2', sx:{whiteSpace:'pre-wrap', wordBreak:'break-word'}},
                      Array.isArray(v) ? v.join(', ') : String(v)));
                }));
            })
          : fields.filter(function(f){ return detail.responses && detail.responses[f.id] !== undefined; }).map(function(f) {
              var v = detail.responses[f.id];
              return E(Box, {key:f.id, sx:{mb:2}},
                E(Typography, {variant:'subtitle2', color:'text.secondary', fontWeight:600}, f.label),
                E(Typography, {variant:'body2', sx:{whiteSpace:'pre-wrap', wordBreak:'break-word'}},
                  Array.isArray(v) ? v.join(', ') : String(v)));
            }),
        (!detail.responses || Object.keys(detail.responses).length === 0)
          ? E(Alert, {severity:'info'}, 'No form fields are shared with your account.')
          : null),
      E(DialogActions, null,
        E(Button, {onClick:function(){ setDetail(null); }}, 'Close'))) : null);
}
